import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // Use translation hook
import { ApiBaseUrlContext } from '../App';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatDateByLocale } from '../utils/dateUtils'; // Import the locale formatter
import './DetailedServiceReport.css'; 

const DetailedServiceReport = () => {
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [currencyPosition, setCurrencyPosition] = useState('right');
  const { t, i18n } = useTranslation(); // Include i18n for locale
  const { id } = useParams();
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const [reportDetails, setReportDetails] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/settings`);
        const settings = response.data;
        setCurrencySymbol(settings.currencySymbol || 'kr'); // Set currency symbol
        setCurrencyPosition(settings.currencyPosition || 'right'); // Set position
        // ... other settings
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
  
    fetchSettings();
  }, [apiBaseUrl]);
  useEffect(() => {
    const fetchServiceReport = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/mnt/api/service-reports/${id}`);
        setReportDetails(response.data);
      } catch (error) {
        console.error(t('detailedServiceReport.errorFetchingReport'), error);
      }
    };

    fetchServiceReport();
  }, [apiBaseUrl, id, t]);

  const formatEpochTime = (epochTime) => {
    if (epochTime > 9999999999) {
      return new Date(epochTime).toLocaleString();
    }
    return new Date(epochTime * 1000).toLocaleString();
  };

  const formatDate = (epochTime) => {
    const date = new Date(epochTime);
    return date.toLocaleDateString();
  };

  const formatTime = (epochTime) => {
    const date = new Date(epochTime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const calculateDuration = (start, end) => {
    const startTime = new Date(start);
    const endTime = new Date(end);
    const totalMinutes = Math.floor((endTime - startTime) / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes, durationText: `${hours}h ${minutes}m` };
  };

  const calculateCost = (hourlyRate, start, end) => {
    const { hours, minutes } = calculateDuration(start, end);
    const totalHours = hours + minutes / 60;
    return hourlyRate * totalHours;
  };

  const generatePDF = async () => {
    const doc = new jsPDF();
  
    // ✅ Load the company logo
    const logoUrl = `${apiBaseUrl}/mnt/api/logo`; // API endpoint for the logo
  
    try {
      const response = await fetch(logoUrl);
      if (response.ok) {
        const blob = await response.blob();
        const reader = new FileReader();
  
        // ✅ Wait for the FileReader to complete before adding the image
        const logoData = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
  
        // ✅ Add Logo at the Top Left (with some padding)
        doc.addImage(logoData, 'PNG', 20, 13, 42, 14); // (x, y, width, height)
      } else {
        console.error('Failed to load company logo.');
      }
    } catch (error) {
      console.error('Error fetching company logo:', error);
    }
      
    // Title
    doc.setFontSize(18);
    doc.text(t('detailedServiceReport.serviceReport'), 105, 20, null, null, 'center');
    doc.setFontSize(12);
    doc.text(`${t('detailedServiceReport.reportId')}: ${report.servicereportid}`, 105, 30, null, null, 'center');

    // Created, Updated, Status, Technicians
    doc.setFontSize(10);
    doc.text(`${t('detailedServiceReport.createdAt')}: ${formatDateByLocale(Math.floor(report.created_at / 1000), i18n.language)}`, 20, 40);
    doc.text(`${t('detailedServiceReport.updatedAt')}: ${formatDateByLocale(Math.floor(report.updated_at / 1000), i18n.language)}`, 20, 45);

    doc.text(`${t('detailedServiceReport.status')}: ${report.status}`, 140, 40);
    doc.text(`${t('detailedServiceReport.technicians')}: ${technicians.map(tech => tech.technician).join(', ')}`, 140, 45);

    // Machine, Fault, and Description
    doc.setFontSize(12);
    doc.text(`${t('detailedServiceReport.machine')}:`, 20, 55);
    doc.text(report.machine, 60, 55);

    doc.text(`${t('detailedServiceReport.fault')}:`, 20, 65);
    doc.text(report.fault, 60, 65);

    // Calculate the height needed for the description
    doc.setFontSize(12);
    const descriptionLines = doc.splitTextToSize(report.description_action, 130);
    const descriptionHeight = descriptionLines.length * doc.getTextDimensions('Text').h + 5; // Add some padding

    doc.text(`${t('detailedServiceReport.description')}:`, 20, 75);
    doc.text(descriptionLines, 60, 75);

    // Time Entries
    let startY = 75 + descriptionHeight + 10; // Start the time entries table below the description

    doc.setFontSize(14);
    doc.text(`${t('detailedServiceReport.timeEntries')}:`, 20, startY);

    startY += 5;
    doc.autoTable({
      startY: startY,
      head: [[t('detailedServiceReport.tableHeaders.date'), t('detailedServiceReport.tableHeaders.description'), t('detailedServiceReport.tableHeaders.startTime'), t('detailedServiceReport.tableHeaders.endTime'), t('detailedServiceReport.tableHeaders.duration'), t('detailedServiceReport.tableHeaders.cost')]],
      body: timeEntries.map(entry => [
        formatDate(entry.start_time),
        entry.timedescription,
        formatTime(entry.start_time),
        formatTime(entry.end_time),
        calculateDuration(entry.start_time, entry.end_time).durationText,
`${currencyPosition === 'left' ? `${currencySymbol}${calculateCost(entry.hourly_rate, entry.start_time, entry.end_time).toFixed(2)}` : `${calculateCost(entry.hourly_rate, entry.start_time, entry.end_time).toFixed(2)}${currencySymbol}`}`
      ]),
      theme: 'grid',
      headStyles: { fillColor: [160, 160, 160] },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 'wrap' },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 20 },
        5: { cellWidth: 20 },
      },
      styles: { overflow: 'linebreak' },
    });

    // Calculate total time cost
    const totalTimeCost = timeEntries.reduce((sum, entry) => {
      return sum + calculateCost(entry.hourly_rate, entry.start_time, entry.end_time);
    }, 0);

    // Add Materials Table
    startY = doc.autoTable.previous.finalY + 10;
    doc.setFontSize(14);
    doc.text(`${t('detailedServiceReport.materials')}:`, 20, startY);

    startY += 5;
    doc.autoTable({
      startY: startY,
      head: [[t('detailedServiceReport.tableHeaders.description'), t('detailedServiceReport.tableHeaders.quantity'), t('detailedServiceReport.tableHeaders.unit'), t('detailedServiceReport.tableHeaders.price'), t('detailedServiceReport.tableHeaders.sum')]],
      body: materials.map(material => [
        material.description,
        material.quantity,
        material.unit,
`${currencyPosition === 'left' ? `${currencySymbol}${material.price.toFixed(2)}` : `${material.price.toFixed(2)}${currencySymbol}`}`,
`${currencyPosition === 'left' ? `${currencySymbol}${(material.price * material.quantity).toFixed(2)}` : `${(material.price * material.quantity).toFixed(2)}${currencySymbol}`}`

      ]),
      theme: 'grid',
      headStyles: { fillColor: [160, 160, 160] },
      columnStyles: {
        0: { cellWidth: 'wrap' },
        1: { cellWidth: 20 },
        2: { cellWidth: 20 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
      },
      styles: { overflow: 'linebreak' },
    });

    // Calculate total material cost
    const totalMaterialCost = materials.reduce((sum, material) => {
      return sum + material.price * material.quantity;
    }, 0);

    // Calculate grand total
    const grandTotal = totalTimeCost + totalMaterialCost;

    // Add Total Costs
    startY = doc.autoTable.previous.finalY + 10;
    doc.setFontSize(12);
    doc.text(`${t('detailedServiceReport.totalTimeCost')}: ${currencyPosition === 'left' ? `${currencySymbol}${totalTimeCost.toFixed(2)}` : `${totalTimeCost.toFixed(2)}${currencySymbol}`}`, 20, startY);
    doc.text(`${t('detailedServiceReport.totalMaterialCost')}: ${currencyPosition === 'left' ? `${currencySymbol}${totalMaterialCost.toFixed(2)}` : `${totalMaterialCost.toFixed(2)}${currencySymbol}`}`, 20, startY + 5);
    doc.text(`${t('detailedServiceReport.grandTotal')}: ${currencyPosition === 'left' ? `${currencySymbol}${grandTotal.toFixed(2)}` : `${grandTotal.toFixed(2)}${currencySymbol}`}`, 20, startY + 10);
    

    // Save the PDF
    doc.save(`Service_Report_${report.servicereportid}.pdf`);
  };

  if (!reportDetails) {
    return <div>{t('detailedServiceReport.loading')}</div>;
  }

  const { report, technicians, timeEntries, materials, files } = reportDetails;

  // Calculate Total Time Cost
  const totalTimeCost = timeEntries.reduce((sum, entry) => {
    return sum + calculateCost(entry.hourly_rate, entry.start_time, entry.end_time);
  }, 0);

  // Calculate Total Material Cost
  const totalMaterialCost = materials.reduce((sum, material) => {
    return sum + material.price * material.quantity;
  }, 0);

  // Calculate Grand Total
  const grandTotal = totalTimeCost + totalMaterialCost;

  return (

     <div className="content">

        <div className="top-menu">
          <button type="button" className="" onClick={() => navigate(-1)}>{t('detailedServiceReport.back')}</button>
          <div class="top-menu__button-space"></div>
          <button type="button" className="" onClick={generatePDF}>{t('detailedServiceReport.downloadPDF')}</button>
        </div>

        <div className='dsr-container'>

        <div className="dsr-container__logo-container">
            <img 
              src={`${apiBaseUrl}/mnt/api/logo`} 
              alt="Company Logo" 
              className="company-logo"
            />
          </div>
           
          <h2>{t('detailedServiceReport.serviceReport')} {report.servicereportid}</h2>

          
          
          <div className='dsr-container__data'>
          
            <div>
              <p>{t('detailedServiceReport.createdAt')}: {formatDateByLocale(Math.floor(report.created_at / 1000), i18n.language)}</p>
              <p>{t('detailedServiceReport.updatedAt')}: {formatDateByLocale(Math.floor(report.updated_at / 1000), i18n.language)}</p>
            </div>
            <div>
              <p>{t('detailedServiceReport.status')}: {report.status}</p>
              <p>{t('detailedServiceReport.technicians')}: {technicians.map(tech => tech.technician).join(', ')}</p>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <p>{t('detailedServiceReport.machine')}: {report.machine}</p>
            <p>{t('detailedServiceReport.fault')}: {report.fault}</p>
            <p>{t('detailedServiceReport.description')}: {report.description_action}</p>
          </div>

          <div className='dsr-container__time-registration'>
            <h3>{t('detailedServiceReport.timeEntries')}:</h3>
            <div className='table-container'>
              <table>
                <thead>
                  <tr>
                    <th>{t('detailedServiceReport.tableHeaders.date')}</th>
                    <th className='max-width'>{t('detailedServiceReport.tableHeaders.description')}</th>
                    <th>{t('detailedServiceReport.tableHeaders.startTime')}</th>
                    <th>{t('detailedServiceReport.tableHeaders.endTime')}</th>
                    <th className='dsr-container__align-right'>{t('detailedServiceReport.tableHeaders.duration')}</th>
                    <th className='dsr-container__align-right'>{t('detailedServiceReport.tableHeaders.cost')}</th>
                  </tr>
                </thead>
                <tbody>
                {timeEntries.map((entry, index) => {
                  const cost = calculateCost(entry.hourly_rate, entry.start_time, entry.end_time);
                  const duration = calculateDuration(entry.start_time, entry.end_time).durationText;

                  return (
                    <tr key={index}>
                      {/* Start Date */}
                      <td className='no-wrap'>
                        {formatDateByLocale(Math.floor(entry.start_time / 1000), i18n.language, false)}
                      </td>

                      {/* Time Description */}
                      <td>
                        {entry.timedescription}
                      </td>

                      {/* Start Time */}
                      <td>
                        {formatDateByLocale(Math.floor(entry.start_time / 1000), 'timeOnly')}
                      </td>

                      {/* End Time */}
                      <td>
                        {formatDateByLocale(Math.floor(entry.start_time / 1000), 'timeOnly')}
                      </td>

                      {/* Duration */}
                      <td className='dsr-container__align-right no-wrap'>
                        {duration}
                      </td>

                      {/* Cost */}
                      <td className='dsr-container__align-right'>
                        {currencyPosition === 'left'
                          ? `${currencySymbol}${cost.toFixed(2)}`
                          : `${cost.toFixed(2)}${currencySymbol}`}
                      </td>
                    </tr>
                  );
                })}

                </tbody>
              </table>
            </div>
            <div className='dsr-containert__time-registration-total'>
                {t('detailedServiceReport.totalTimeCost')}: {currencyPosition === 'left' ? `${currencySymbol}${totalTimeCost.toFixed(2)}` : `${totalTimeCost.toFixed(2)}${currencySymbol}`}
            </div>
          </div>

          <div className='dsr-container__material'>
            <h3>{t('detailedServiceReport.materials')}:</h3>
            <div className='table-container'>
              <table>
                <thead>
                  <tr>
                    <th className='max-width'>{t('detailedServiceReport.tableHeaders.description')}</th>
                    <th className='dsr-container__align-right'>{t('detailedServiceReport.tableHeaders.quantity')}</th>
                    <th className='dsr-container__align-right'>{t('detailedServiceReport.tableHeaders.unit')}</th>
                    <th className='dsr-container__align-right'>{t('detailedServiceReport.tableHeaders.price')}</th>
                    <th className='dsr-container__align-right'>{t('detailedServiceReport.tableHeaders.sum')}</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((material, index) => (
                    <tr key={index}>
                      <td>{material.description}</td>
                      <td className='dsr-container__align-right'>{material.quantity}</td>
                      <td className='dsr-container__align-right'>{material.unit}</td>
                      <td className='dsr-container__align-right'>
                        {currencyPosition === 'left' ? `${currencySymbol}${material.price.toFixed(2)}` : `${material.price.toFixed(2)}${currencySymbol}`}
                      </td>
                      <td className='dsr-container__align-right'>
                        {currencyPosition === 'left' ? `${currencySymbol}${(material.price * material.quantity).toFixed(2)}` : `${(material.price * material.quantity).toFixed(2)}${currencySymbol}`}
                     </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='dsr-containert__time-registration-total'>
              {t('detailedServiceReport.totalMaterialCost')}: {currencyPosition === 'left' ? `${currencySymbol}${totalMaterialCost.toFixed(2)}` : `${totalMaterialCost.toFixed(2)}${currencySymbol}`}
            </div>
          </div>

          <div className='dsr-container__grandtotal'>
            <h3>{t('detailedServiceReport.grandTotal')}: {currencyPosition === 'left' ? `${currencySymbol}${grandTotal.toFixed(2)}` : `${grandTotal.toFixed(2)}${currencySymbol}`}</h3>
          </div>

          <div className='dsr-container__files'>
            <h3>{t('detailedServiceReport.files')}:</h3>
            <ul>
              {files.map((file, index) => (
                <li key={index}>
                  <a href={`${apiBaseUrl}${file.filepath ? file.filepath.replace(/\\/g, '/') : ''}`} target="_blank" rel="noopener noreferrer">
                    {file.filename}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>


  );
};

export default DetailedServiceReport;
